import { ReactNode, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Bot,
  ChevronLeft,
  MessagesSquare,
  Save,
  Trash2,
} from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { Avatar } from "../../components/Avatar/Avatar";
import { Badge } from "../../components/Badge/Badge";
import { Button } from "../../components/Button/Button";
import { CountryCell } from "../../components/CountryCell/CountryCell";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { Select } from "../../components/Select/Select";
import { Skeleton } from "../../components/Skeleton/Skeleton";
import { useToast } from "../../components/Toast/toastContext";

import { ApiError, describeError } from "../../lib/errors";
import { guildQueryKey, guildsListQueryKey } from "../guilds/queryKeys";
import { useProject } from "../projects/useProject";
import type { ProfileConfig } from "../projects/config/types";
import {
  getPlayer,
  getPlayerCloudSave,
  getPlayerGifts,
  getPlayerGuild,
  getPlayerIdentities,
  getPlayerLeaderboards,
  getPlayerReferrals,
  getPlayerSignatureFailures,
  updatePlayer,
  type PlayerSignatureFailures,
} from "./api";
import { DeletePlayerDialog } from "./DeletePlayerDialog";
import { formatDate, formatRelative } from "./formatTime";
import {
  playerCloudSaveQueryKey,
  playerDeletionPreviewQueryKey,
  playerGiftsQueryKey,
  playerGuildQueryKey,
  playerIdentitiesQueryKey,
  playerLeaderboardsQueryKey,
  playerQueryKey,
  playerReferralsQueryKey,
  playerSignatureFailuresQueryKey,
  playersListQueryKey,
} from "./queryKeys";
import { PlayerCloudSaveCard } from "./PlayerCloudSaveCard";
import { PlayerGiftsCard } from "./PlayerGiftsCard";
import { PlayerGuildCard } from "./PlayerGuildCard";
import { PlayerLeaderboardsCard } from "./PlayerLeaderboardsCard";
import { PlayerMetaEditor } from "./PlayerMetaEditor";
import { PlayerReferralsCard } from "./PlayerReferralsCard";
import { PlayerStatsEditor } from "./PlayerStatsEditor";
import type {
  DeletionGuild,
  GiftDirection,
  Player,
  PlayerCloudSave,
  PlayerGiftsResponse,
  PlayerGuild,
  PlayerIdentity,
  PlayerLeaderboards,
  PlayerReferralsResponse,
  UpdatePlayerInput,
} from "./types";
import styles from "./PlayerDetailPage.module.css";

// === Formatting helpers ============================================== //

/**
 * The toast after a deletion, built from the verdict the DELETE returned —
 * what happened, not what the preflight predicted while the operator was
 * typing the player's name.
 *
 * The guild is mentioned only when it actually changed hands or ceased to
 * exist: a player merely leaving a guild is not news, and a guild sentence on
 * every deletion would train the operator to stop reading the ones that
 * matter.
 */
function deletedMessage(name: string, guild: DeletionGuild | null): string {
  const gone = `${name} is gone.`;
  if (!guild) return gone;
  if (guild.outcome === "transfer" && guild.successor) {
    return `${gone} ${guild.name} is now led by ${guild.successor.display_name}.`;
  }
  if (guild.outcome === "disband") {
    return `${gone} ${guild.name} was disbanded.`;
  }
  return gone;
}

/**
 * The server's own reading of `cloud_save.max_bytes`: absent or not a number
 * is the 10 MiB default, and anything else is clamped to 1–64 MiB.
 */
function clampSaveBytes(raw: unknown): number {
  const MIB = 1048576;
  if (typeof raw !== "number" || !Number.isFinite(raw)) return 10 * MIB;
  return Math.min(Math.max(raw, MIB), 64 * MIB);
}

// === Component ====================================================== //

export function PlayerDetailPage() {
  const { id = "", playerId = "" } = useParams<{
    id: string;
    playerId: string;
  }>();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { push } = useToast();

  // Bumped after every successful save so PlayerDetailForm remounts clean,
  // re-reading its field state from the freshly-saved server record.
  const [saveGen, setSaveGen] = useState(0);

  // Which side of the Gifts card is showing. It lives on the PAGE, not inside
  // the card: it keys the query below, and the form under it is remounted
  // after every save (that is what `saveGen` above does), so a tab kept in the
  // card would flip back to Sent every time somebody renamed the player.
  const [giftDirection, setGiftDirection] = useState<GiftDirection>("sent");

  // The project — shared cache key with PlayersListPage / TeamPage. Read
  // config.profile for the stats schema + allowed-avatars whitelist.
  const projectQuery = useProject(id);

  // The player record being edited.
  const playerQuery = useQuery({
    queryKey: playerQueryKey(id, playerId),
    queryFn: () => getPlayer(id, playerId),
    enabled: !!id && !!playerId,
  });

  // The player's linked identities. Deliberately absent from the render gate
  // below: the Linked-accounts card carries its own loading and failure state,
  // so a slow or broken identities call never holds back the editor.
  const identitiesQuery = useQuery({
    queryKey: playerIdentitiesQueryKey(id, playerId),
    queryFn: () => getPlayerIdentities(id, playerId),
    enabled: !!id && !!playerId,
  });

  // The signature-failure counter of the identity card's fifth cell. Outside
  // the render gate like the identities and the gifts: it is an indicator, and
  // an indicator that did not answer must cost one cell rather than the page
  // (§8.3). Read once per card — the window is seven days wide and does not
  // move while somebody is looking at it.
  const signatureQuery = useQuery({
    queryKey: playerSignatureFailuresQueryKey(id, playerId),
    queryFn: () => getPlayerSignatureFailures(id, playerId),
    enabled: !!id && !!playerId,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const project = projectQuery.data;
  const player = playerQuery.data;

  // games.config is typed `unknown` — narrow to the profile sub-object.
  const profile = (project?.config as { profile?: ProfileConfig } | undefined)
    ?.profile;
  const statsSchema = profile?.stats_schema ?? [];
  const metaSchema = profile?.meta_schema ?? [];
  const allowedAvatars = profile?.allowed_avatars ?? [];

  // The gift section, narrowed the same way — and with an inline shape rather
  // than the config editor's GiftConfig, because this page asks the config one
  // question and importing that type would tie the player card to the file the
  // config FORM is written against.
  //
  // An empty (or absent) `gift.types` is the project saying gifts do not exist
  // here — there is no separate on/off switch — and then the card is not drawn
  // at all. An empty table would say the mechanic exists and simply went
  // unused, which is a different and untrue answer.
  //
  // `games.config` is a free-form JSONB blob, and this page reads it RAW — it
  // does not go through the config form's `normalizeConfig`. So the shape is
  // checked and not assumed: a hand-edited `"types": "lives"` has a length of
  // five and would draw the card and fire the request, while the server reads
  // that very config as «no gift types at all» (`parseGiftConfig` decodes the
  // key as a list of raw messages and gets nothing).
  const giftTypes = (
    project?.config as { gift?: { types?: unknown } } | undefined
  )?.gift?.types;
  const giftsConfigured = Array.isArray(giftTypes) && giftTypes.length > 0;

  // The cloud-save section, read raw for the same reason. Unlike gifts it
  // has an explicit switch, and the card is drawn only when it is on — off,
  // the game cannot reach the slot either, and a card about a save nobody
  // can write would be a card about nothing. The ceiling is clamped the way
  // the server clamps it (internal/cloudsave/config.go), so the upload
  // dialog refuses exactly what the bucket would.
  const cloudSaveSection = (
    project?.config as
      | { cloud_save?: { enabled?: unknown; max_bytes?: unknown } }
      | undefined
  )?.cloud_save;
  const cloudSavesEnabled = cloudSaveSection?.enabled === true;
  const cloudSaveMaxBytes = clampSaveBytes(cloudSaveSection?.max_bytes);
  // Only an explicit «no» from the server disables the card's buttons.
  const cloudSaveStorageConfigured =
    project?.cloud_save_storage_configured !== false;

  const cloudSaveQuery = useQuery({
    queryKey: playerCloudSaveQueryKey(id, playerId),
    queryFn: () => getPlayerCloudSave(id, playerId),
    enabled: !!id && !!playerId && cloudSavesEnabled,
  });

  // The invite section, read raw for the same reason the two above are — this
  // page never runs the config form's `normalizeConfig`. The switch is the one
  // question asked of it: off, the game cannot accept a code either, and a card
  // about a mechanic nobody can reach would be a card about nothing.
  const invitesSection = (
    project?.config as { invites?: { enabled?: unknown } } | undefined
  )?.invites;
  const invitesEnabled = invitesSection?.enabled === true;

  const referralsQuery = useQuery({
    queryKey: playerReferralsQueryKey(id, playerId),
    queryFn: () => getPlayerReferrals(id, playerId),
    enabled: !!id && !!playerId && invitesEnabled,
  });

  // One direction of the player's gifts. Outside the render gate for the same
  // reason the identities are: the Gifts card draws its own loading and
  // failure states, and neither of them may hold back the editor.
  const giftsQuery = useQuery({
    queryKey: playerGiftsQueryKey(id, playerId, giftDirection),
    queryFn: () => getPlayerGifts(id, playerId, giftDirection),
    enabled: !!id && !!playerId && giftsConfigured,
    // Flipping the tab changes the key, and without this the card would empty
    // into a skeleton on every flip — including the four numbers at the top,
    // which are the same four in both answers. The rows are NOT drawn from the
    // held-over answer; `isPlaceholderData` tells the card they belong to the
    // other direction.
    placeholderData: (prev: PlayerGiftsResponse | undefined) => prev,
  });

  // The player's guild and the running cycles they stand on. Outside the
  // render gate like the identities: both cards draw their own loading and
  // failure states, and neither may hold back the editor.
  const guildQuery = useQuery({
    queryKey: playerGuildQueryKey(id, playerId),
    queryFn: () => getPlayerGuild(id, playerId),
    enabled: !!id && !!playerId,
  });

  const leaderboardsQuery = useQuery({
    queryKey: playerLeaderboardsQueryKey(id, playerId),
    queryFn: () => getPlayerLeaderboards(id, playerId),
    enabled: !!id && !!playerId,
  });

  const header = (
    <PageHeader
      breadcrumb={[
        { label: project?.name ?? "Project" },
        { label: "Players", to: `/projects/${id}/players` },
        { label: player?.display_name ?? "Player" },
      ]}
      title={player?.display_name ?? "Player"}
      actions={
        <>
          {/* The chat stream holds living players only. */}
          {player && !player.is_bot && (
            <Link
              to={`/projects/${id}/chat?player=${encodeURIComponent(playerId)}`}
              className={styles.backBtn}
            >
              <MessagesSquare size={13} aria-hidden />
              Messages
            </Link>
          )}
          <Link
            to={`/projects/${id}/players`}
            className={styles.backBtn}
          >
            <ChevronLeft size={13} aria-hidden />
            Back to players
          </Link>
        </>
      }
    />
  );

  // === Not-found state =============================================== //
  if (
    playerQuery.isError &&
    playerQuery.error instanceof ApiError &&
    playerQuery.error.status === 404
  ) {
    return (
      <>
        {header}
        <div className={styles.page}>
          <div className={styles.content}>
            <div className={styles.errorCard}>
              <Alert variant="danger" title="Player not found">
                This player doesn&apos;t exist, or it belongs to another
                project.
              </Alert>
            </div>
          </div>
        </div>
      </>
    );
  }

  // === Generic load error =========================================== //
  if (projectQuery.isError || playerQuery.isError) {
    const err =
      playerQuery.error instanceof ApiError
        ? playerQuery.error
        : projectQuery.error instanceof ApiError
          ? projectQuery.error
          : null;
    return (
      <>
        {header}
        <div className={styles.page}>
          <div className={styles.content}>
            <div className={styles.errorCard}>
              <Alert variant="danger" title="Couldn't load player">
                {err ? describeError(err.code) : describeError("internal")}
              </Alert>
              <div className={styles.errorActions}>
                <Button
                  variant="secondary"
                  onClick={() => {
                    void projectQuery.refetch();
                    void playerQuery.refetch();
                  }}
                >
                  Retry
                </Button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // === Loading skeleton ============================================= //
  // Both queries must resolve before the editor mounts — PlayerStatsEditor
  // reads `schema` only at first mount, so we never render it with an empty
  // schema and swap the real one in later.
  if (projectQuery.isLoading || playerQuery.isLoading || !project || !player) {
    return (
      <>
        {header}
        <div className={styles.page}>
          <div className={styles.content}>
            <LoadingSkeleton />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {header}
      <PlayerDetailForm
        key={`${player.updated_at}:${saveGen}`}
        projectId={id}
        playerId={playerId}
        player={player}
        statsSchema={statsSchema}
        metaSchema={metaSchema}
        allowedAvatars={allowedAvatars}
        identities={identitiesQuery.data?.identities}
        identitiesLoading={identitiesQuery.isLoading}
        identitiesFailed={identitiesQuery.isError}
        signatureFailures={signatureQuery.data}
        signatureFailuresLoading={signatureQuery.isLoading}
        signatureFailuresFailed={signatureQuery.isError}
        guild={guildQuery.data}
        guildLoading={guildQuery.isLoading}
        guildFailed={guildQuery.isError}
        leaderboards={leaderboardsQuery.data}
        leaderboardsLoading={leaderboardsQuery.isLoading}
        leaderboardsFailed={leaderboardsQuery.isError}
        giftsEnabled={giftsConfigured}
        gifts={giftsQuery.data}
        giftsLoading={giftsQuery.isLoading}
        giftsFailed={giftsQuery.isError}
        giftsStale={giftsQuery.isPlaceholderData}
        giftDirection={giftDirection}
        onGiftDirectionChange={setGiftDirection}
        invitesEnabled={invitesEnabled}
        referrals={referralsQuery.data}
        referralsLoading={referralsQuery.isLoading}
        referralsFailed={referralsQuery.isError}
        cloudSaveEnabled={cloudSavesEnabled}
        cloudSave={cloudSaveQuery.data}
        cloudSaveLoading={cloudSaveQuery.isLoading}
        cloudSaveFailed={cloudSaveQuery.isError}
        cloudSaveStorageConfigured={cloudSaveStorageConfigured}
        cloudSaveMaxBytes={cloudSaveMaxBytes}
        onCloudSaveChanged={() =>
          // Awaited by the dialogs before they close, so the card never
          // shows the old facts under a «done» toast. The deletion preview
          // carries has_cloud_save and goes stale with the slot too.
          Promise.all([
            qc.invalidateQueries({
              queryKey: playerCloudSaveQueryKey(id, playerId),
            }),
            qc.invalidateQueries({
              queryKey: playerDeletionPreviewQueryKey(id, playerId),
            }),
            qc.invalidateQueries({ queryKey: ["project", id, "activity"] }),
          ]).then(() => undefined)
        }
        onSaved={(updated) => {
          // Re-sync the form from the server response: write it into the
          // shared cache so `player` reflects the saved record, then bump
          // `saveGen` to remount PlayerDetailForm clean (its useState
          // initializers re-read from the response; the stats editor
          // remounts with it). `invalidateQueries` then refetches in the
          // background to pick up anything else the server changed.
          qc.setQueryData<Player>(playerQueryKey(id, playerId), updated);
          // `exact` keeps this off the identities query, which shares the key
          // prefix but nothing a profile PATCH can change.
          void qc.invalidateQueries({
            queryKey: playerQueryKey(id, playerId),
            exact: true,
          });
          // Refresh the project's activity feed so the new "player.updated"
          // event surfaces on the overview page.
          void qc.invalidateQueries({ queryKey: ["project", id, "activity"] });
          setSaveGen((g) => g + 1);
          push({ variant: "success", message: "Player updated" });
        }}
        onDeleted={(deletedHere, guild) => {
          // The card this page renders no longer has a record behind it, so
          // there is nowhere to stay: the list is the only honest destination.
          //
          // The list's cache is THROWN AWAY, not marked stale: the page we are
          // about to land on is not mounted, so an invalidation would only
          // flag it, and on mount TanStack Query serves the cache first and
          // refetches behind it — the first frame of the table would show the
          // player we just deleted. Dropping the entry costs the cached pages
          // and searches of this project and buys a skeleton instead of a lie.
          qc.removeQueries({ queryKey: playersListQueryKey(id) });
          void qc.invalidateQueries({ queryKey: ["project", id, "activity"] });
          qc.removeQueries({ queryKey: playerQueryKey(id, playerId) });
          // The guild the server just reported on, for the same reason and
          // without asking which outcome it was: `leave` leaves a stale
          // member_count behind, `transfer` a stale leader_name, `disband` a
          // guild that is not there at all. In all three the list and the card
          // now describe a guild as it was before this deletion, and the guild
          // screens are not mounted to be refetched by an invalidation.
          if (guild) {
            qc.removeQueries({ queryKey: guildsListQueryKey(id) });
            qc.removeQueries({ queryKey: guildQueryKey(id, guild.id) });
          }
          navigate(`/projects/${id}/players`);
          // Only OUR deletion is announced. Arriving at "somebody deleted this
          // player first" and being congratulated on deleting them is how an
          // operator comes to believe they did something they did not do.
          if (deletedHere) {
            push({
              variant: "success",
              title: "Player deleted",
              message: deletedMessage(player.display_name, guild),
            });
          }
        }}
      />
    </>
  );
}

// === Editable form =================================================== //
// Remounted (via the parent's `saveGen` key) after a successful save so its
// useState initializers re-read the freshly-saved record. Discard resets in
// place; the stats editor — uncontrolled after mount — is remounted by its own
// `statsKey` on discard.

interface PlayerDetailFormProps {
  projectId: string;
  playerId: string;
  player: Player;
  statsSchema: ProfileConfig["stats_schema"];
  metaSchema: ProfileConfig["meta_schema"];
  allowedAvatars: string[];
  /** Linked identities — undefined while the (non-blocking) query is in flight. */
  identities: PlayerIdentity[] | undefined;
  identitiesLoading: boolean;
  identitiesFailed: boolean;
  /** The signature-failure counter, on the same non-blocking contract. */
  signatureFailures: PlayerSignatureFailures | undefined;
  signatureFailuresLoading: boolean;
  signatureFailuresFailed: boolean;
  /** The player's guild — null for none, undefined while in flight. */
  guild: PlayerGuild | null | undefined;
  guildLoading: boolean;
  guildFailed: boolean;
  /** The running cycles the player stands on — same non-blocking contract. */
  leaderboards: PlayerLeaderboards | undefined;
  leaderboardsLoading: boolean;
  leaderboardsFailed: boolean;
  /** Whether this project declares any gift types at all. No types, no card. */
  giftsEnabled: boolean;
  /** The gifts of `giftDirection` — same non-blocking contract as above. */
  gifts: PlayerGiftsResponse | undefined;
  giftsLoading: boolean;
  giftsFailed: boolean;
  /** `gifts` is the previous direction's answer while this one is in flight. */
  giftsStale: boolean;
  giftDirection: GiftDirection;
  onGiftDirectionChange: (next: GiftDirection) => void;
  /** Whether the project's `invites.enabled` is on. Off, no card. */
  invitesEnabled: boolean;
  /** Both sides of the player's invites — same non-blocking contract as above. */
  referrals: PlayerReferralsResponse | undefined;
  referralsLoading: boolean;
  referralsFailed: boolean;
  /** Whether the project's `cloud_save.enabled` is on. Off, no card. */
  cloudSaveEnabled: boolean;
  /** The slot's facts — same non-blocking contract as the gifts. */
  cloudSave: PlayerCloudSave | undefined;
  cloudSaveLoading: boolean;
  cloudSaveFailed: boolean;
  /** Whether THIS SERVER has a bucket; false disables the three actions. */
  cloudSaveStorageConfigured: boolean;
  /** The project's ceiling on one save, for the upload dialog. */
  cloudSaveMaxBytes: number;
  /** Called after a download, an upload or a delete: refetch the card and the feed. */
  onCloudSaveChanged: () => void;
  /** Called with the saved record after a successful PATCH. */
  onSaved: (updated: Player) => void;
  /**
   * Called once the player is gone from the server: whether THIS card's dialog
   * is what removed them, and the guild verdict the server returned for it.
   */
  onDeleted: (deletedHere: boolean, guild: DeletionGuild | null) => void;
}

function PlayerDetailForm({
  projectId,
  playerId,
  player,
  statsSchema,
  metaSchema,
  allowedAvatars,
  identities,
  identitiesLoading,
  identitiesFailed,
  signatureFailures,
  signatureFailuresLoading,
  signatureFailuresFailed,
  guild,
  guildLoading,
  guildFailed,
  leaderboards,
  leaderboardsLoading,
  leaderboardsFailed,
  giftsEnabled,
  gifts,
  giftsLoading,
  giftsFailed,
  giftsStale,
  giftDirection,
  onGiftDirectionChange,
  invitesEnabled,
  referrals,
  referralsLoading,
  referralsFailed,
  cloudSaveEnabled,
  cloudSave,
  cloudSaveLoading,
  cloudSaveFailed,
  cloudSaveStorageConfigured,
  cloudSaveMaxBytes,
  onCloudSaveChanged,
  onSaved,
  onDeleted,
}: PlayerDetailFormProps) {
  // The confirmation dialog. It is opened from the danger zone below and
  // loads its own preflight — nothing about a deletion is fetched until
  // somebody asks for one.
  const [deleteOpen, setDeleteOpen] = useState(false);
  // A server that predates players.meta (or any partial record) must not
  // hand the uncontrolled editors an undefined object.
  const playerMeta = player.meta ?? {};

  // --- Profile field state ------------------------------------------- //
  const [displayName, setDisplayName] = useState(player.display_name);
  const [avatar, setAvatar] = useState(player.avatar ?? "");

  // --- Stats editor state -------------------------------------------- //
  // PlayerStatsEditor is uncontrolled after mount. We mirror its latest value
  // here, track whether it has been touched, and remount it (via statsKey) on
  // discard so it resets back to the player's stats.
  const [stats, setStats] = useState<Record<string, unknown>>(player.stats);
  const [statsDirty, setStatsDirty] = useState(false);
  const [statsValid, setStatsValid] = useState(true);
  const [statsKey, setStatsKey] = useState(0);

  // --- Meta editor state --------------------------------------------- //
  // Same uncontrolled contract as the stats editor above.
  const [meta, setMeta] = useState<Record<string, unknown>>(playerMeta);
  const [metaDirty, setMetaDirty] = useState(false);
  const [metaValid, setMetaValid] = useState(true);
  const [metaKey, setMetaKey] = useState(0);

  // --- Per-field validation ------------------------------------------ //
  const trimmedName = displayName.trim();
  const nameError =
    trimmedName.length < 1 || trimmedName.length > 80
      ? "Display name must be between 1 and 80 characters."
      : undefined;

  // --- Dirty tracking ------------------------------------------------ //
  const nameDirty = displayName !== player.display_name;
  const avatarDirty = avatar !== (player.avatar ?? "");

  const dirty = nameDirty || avatarDirty || statsDirty || metaDirty;

  const changeCount =
    (nameDirty ? 1 : 0) +
    (avatarDirty ? 1 : 0) +
    (statsDirty ? 1 : 0) +
    (metaDirty ? 1 : 0);

  const errorCount =
    (nameError ? 1 : 0) +
    (statsDirty && !statsValid ? 1 : 0) +
    (metaDirty && !metaValid ? 1 : 0);
  const hasError = errorCount > 0;

  // === Save ========================================================== //
  const mutation = useMutation({
    mutationFn: (body: UpdatePlayerInput) =>
      updatePlayer(projectId, playerId, body),
  });

  // 422 field-level errors surfaced from the server response.
  const serverError =
    mutation.error instanceof ApiError ? mutation.error : null;
  const serverAvatarError =
    serverError?.status === 422 && serverError.code === "avatar_not_allowed"
      ? describeError("avatar_not_allowed")
      : undefined;
  const serverStatsError =
    serverError?.status === 422 && serverError.code === "invalid_stats"
      ? describeError("invalid_stats")
      : undefined;
  const serverMetaError =
    serverError?.status === 422 && serverError.code === "invalid_meta"
      ? describeError("invalid_meta")
      : undefined;
  const serverGenericError =
    serverError && !serverAvatarError && !serverStatsError && !serverMetaError
      ? describeError(serverError.code)
      : undefined;

  function doSave() {
    if (hasError || !dirty) return;
    // Only the dirty field groups are sent. `stats` is omitted entirely when
    // the editor is clean so the server doesn't re-validate untouched legacy
    // keys against the schema.
    const body: UpdatePlayerInput = {};
    if (nameDirty) body.display_name = trimmedName;
    if (avatarDirty) body.avatar = avatar;
    if (statsDirty) body.stats = stats;
    if (metaDirty) body.meta = meta;
    mutation.mutate(body, {
      onSuccess: (updated) => {
        onSaved(updated);
      },
    });
  }

  function doDiscard() {
    setDisplayName(player.display_name);
    setAvatar(player.avatar ?? "");
    setStats(player.stats);
    setStatsDirty(false);
    setStatsValid(true);
    setMeta(playerMeta);
    setMetaDirty(false);
    setMetaValid(true);
    // Remount both editors so they re-seed from the player's record.
    setStatsKey((k) => k + 1);
    setMetaKey((k) => k + 1);
    mutation.reset();
  }

  const useAvatarSelect = allowedAvatars.length > 0;

  return (
    <>
      <div className={styles.page}>
        <div className={styles.content}>
          {serverGenericError && (
            <div className={styles.errorAlert}>
              <Alert variant="danger" title="Couldn't save changes">
                {serverGenericError}
              </Alert>
            </div>
          )}

          {/* ───────── Identity (read-only) ───────── */}
          <div className={styles.card}>
            <div className={styles.cardBody}>
              <div className={styles.identity}>
                <Avatar
                  name={player.display_name}
                  image={player.avatar_url ?? undefined}
                  size={52}
                />
                <div>
                  <div className={styles.identityName}>
                    {player.display_name}
                  </div>
                  <div className={styles.identitySub}>
                    External ID ·{" "}
                    <span className={styles.mono}>{player.external_id}</span>
                  </div>
                  <MetaSummary schema={metaSchema} meta={player.meta ?? {}} />
                </div>
              </div>
              <div className={styles.kvGrid}>
                <div className={styles.kv}>
                  <div className={styles.kvKey}>Player UUID</div>
                  <div className={`${styles.kvVal} ${styles.mono}`}>
                    {player.id}
                  </div>
                </div>
                <div className={styles.kv}>
                  <div className={styles.kvKey}>Country</div>
                  <div className={styles.kvVal}>
                    <CountryCell code={player.country} variant="name" />
                  </div>
                </div>
                <div className={styles.kv}>
                  <div className={styles.kvKey}>Joined</div>
                  <div className={styles.kvVal}>
                    {formatDate(player.created_at)}
                  </div>
                </div>
                <div className={styles.kv}>
                  <div className={styles.kvKey}>Last updated</div>
                  <div className={styles.kvVal}>
                    {formatRelative(player.updated_at)}
                  </div>
                </div>
                <SignatureFailuresCell
                  data={signatureFailures}
                  loading={signatureFailuresLoading}
                  failed={signatureFailuresFailed}
                />
              </div>
            </div>
          </div>

          {/* ───────── Guild (read-only) ─────────
              Right under Identity and always drawn: which guild a player is
              in is part of who they are, whatever the project's config. */}
          <div className={styles.guildCard}>
            <PlayerGuildCard
              projectId={projectId}
              data={guild}
              loading={guildLoading}
              failed={guildFailed}
            />
          </div>

          {/* ───────── Linked accounts (read-only) ───────── */}
          <LinkedAccountsCard
            identities={identities}
            loading={identitiesLoading}
            failed={identitiesFailed}
            playerAvatarUrl={player.avatar_url}
          />

          {/* ───────── Cloud save (t-156 player-cloud-save.html) ─────────
              Between Linked accounts and Profile, beside Gifts: it describes
              what the player HAS, not a field an operator edits. Absent
              entirely while the project's switch is off. */}
          {cloudSaveEnabled && (
            <div className={styles.cloudSaveCard}>
              <PlayerCloudSaveCard
                projectId={projectId}
                player={player}
                data={cloudSave}
                loading={cloudSaveLoading}
                failed={cloudSaveFailed}
                storageConfigured={cloudSaveStorageConfigured}
                maxBytes={cloudSaveMaxBytes}
                onChanged={onCloudSaveChanged}
              />
            </div>
          )}

          {/* ───────── Gifts (read-only) ─────────
              After Linked accounts and before Profile: it describes what the
              player DID, not what an operator may edit, so it stands with the
              read-only half above everything editable.

              Absent entirely when the project declares no gift types — see the
              narrowing on the page above. */}
          {giftsEnabled && (
            <div className={styles.giftsCard}>
              <PlayerGiftsCard
                projectId={projectId}
                direction={giftDirection}
                onDirectionChange={onGiftDirectionChange}
                data={gifts}
                loading={giftsLoading}
                failed={giftsFailed}
                stale={giftsStale}
              />
            </div>
          )}

          {/* ───────── Invites (t-162 player-detail-invites.html) ─────────
              After Gifts and before Profile, with the rest of the read-only
              half: it says who brought this player in and whom they brought
              in — not a field an operator edits.

              Absent entirely while the project's switch is off, and then
              nothing is asked for either — see the narrowing on the page. */}
          {invitesEnabled && (
            <div className={styles.invitesCard}>
              <PlayerReferralsCard
                projectId={projectId}
                data={referrals}
                loading={referralsLoading}
                failed={referralsFailed}
              />
            </div>
          )}

          {/* ───────── Leaderboards (read-only) ─────────
              Last of the read-only half, before Profile, and always drawn:
              a player on no board is an answer the card states itself. */}
          <div className={styles.leaderboardsCard}>
            <PlayerLeaderboardsCard
              projectId={projectId}
              data={leaderboards}
              loading={leaderboardsLoading}
              failed={leaderboardsFailed}
            />
          </div>

          {/* ───────── Profile ───────── */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3 className={styles.cardTitle}>Profile</h3>
              <span className={styles.cardSub}>display name · avatar</span>
            </div>
            <div className={styles.cardBody}>
              <div className={styles.formGrid2}>
                {/* Display name */}
                <div className={styles.inputGroup}>
                  <label htmlFor="player-display-name">
                    Display name
                    {nameDirty && (
                      <span
                        className={styles.modDot}
                        title="modified"
                        aria-hidden
                      />
                    )}
                  </label>
                  <input
                    id="player-display-name"
                    className={`${styles.input} ${
                      nameError ? styles.invalid : ""
                    } ${!nameError && nameDirty ? styles.modified : ""}`}
                    value={displayName}
                    aria-invalid={nameError ? true : undefined}
                    aria-describedby={nameError ? "player-name-err" : undefined}
                    onChange={(e) => setDisplayName(e.target.value)}
                  />
                  {nameError && (
                    <div className={styles.fieldError} id="player-name-err">
                      {nameError}
                    </div>
                  )}
                </div>

                {/* Avatar */}
                <div className={styles.inputGroup}>
                  <label htmlFor="player-avatar">
                    Avatar
                    {avatarDirty && (
                      <span
                        className={styles.modDot}
                        title="modified"
                        aria-hidden
                      />
                    )}
                  </label>
                  {useAvatarSelect ? (
                    <Select
                      id="player-avatar"
                      value={avatar}
                      onChange={setAvatar}
                      options={allowedAvatars.map((a) => ({
                        value: a,
                        label: a,
                      }))}
                      placeholder="Select an avatar…"
                      monospaceValue
                    />
                  ) : (
                    <input
                      id="player-avatar"
                      className={`${styles.input} ${styles.mono} ${
                        !avatarDirty ? "" : styles.modified
                      }`}
                      type="text"
                      value={avatar}
                      onChange={(e) => setAvatar(e.target.value)}
                    />
                  )}
                  {serverAvatarError ? (
                    <div className={styles.fieldError}>{serverAvatarError}</div>
                  ) : useAvatarSelect ? (
                    <div className={styles.helper}>
                      From the project&apos;s allowed-avatars whitelist.
                    </div>
                  ) : (
                    <div className={styles.helper}>
                      Whitelist <code className={styles.code}>allowed_avatars</code>{" "}
                      is empty → free-text input, any string accepted.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ───────── Meta ─────────
              Cosmetics sit above Stats: they are closer to the player's
              identity than the gameplay counters below. */}
          {serverMetaError && (
            <div className={styles.errorAlert}>
              <Alert variant="danger" title="Meta rejected">
                {serverMetaError}
              </Alert>
            </div>
          )}
          <div className={styles.metaCard}>
            <PlayerMetaEditor
              key={metaKey}
              schema={metaSchema}
              value={playerMeta}
              onChange={(next) => {
                setMeta(next);
                setMetaDirty(!jsonObjectEqual(next, playerMeta));
              }}
              onValidityChange={setMetaValid}
            />
          </div>

          {/* ───────── Stats ───────── */}
          {serverStatsError && (
            <div className={styles.errorAlert}>
              <Alert variant="danger" title="Stats rejected">
                {serverStatsError}
              </Alert>
            </div>
          )}
          <div className={styles.statsCard}>
            <PlayerStatsEditor
              key={statsKey}
              schema={statsSchema}
              value={player.stats}
              onChange={(next) => {
                setStats(next);
                setStatsDirty(!jsonObjectEqual(next, player.stats));
              }}
              onValidityChange={setStatsValid}
            />
          </div>

          {/* ───────── Danger zone ─────────
              Last in the column and visible to everyone who may edit the
              player: there is no separate right to delete one, the same way
              there is none to rewrite one. The list page carries no delete
              button at all — this is the screen where you can see who you are
              about to remove.

              A bot's card gets the same section with the button replaced by a
              way out: a bot is a piece of the simulation, not an account, and
              retiring one is the Bots page's own operation. */}
          <div className={styles.dangerZoneGap} />
          <section className={styles.dangerZone} aria-label="Danger zone">
            <div className={styles.dzHead}>
              <AlertTriangle size={16} aria-hidden="true" />
              <h3>Danger zone</h3>
            </div>
            {player.is_bot ? (
              <div className={styles.dzRow}>
                <div className={styles.dzBody}>
                  <h4>This is a simulation bot</h4>
                  <p>
                    Bots are not accounts and are not deleted from here. Take
                    this one out of the pool on the Bots page — that is where
                    the simulation keeps track of what it has running.
                  </p>
                </div>
                <Link
                  to={`/projects/${projectId}/bots?tab=pool`}
                  className={styles.backBtn}
                >
                  <Bot size={13} aria-hidden />
                  Open Bots → Pool
                </Link>
              </div>
            ) : (
              <div className={styles.dzRow}>
                <div className={styles.dzBody}>
                  <h4>Delete player</h4>
                  <p>
                    Removes the account for good: the player leaves their guild,
                    loses every friend, request and donation, and drops out of
                    the live leaderboards. Chat history stays. This cannot be
                    undone.
                  </p>
                </div>
                <Button
                  variant="danger"
                  onClick={() => setDeleteOpen(true)}
                  icon={<Trash2 size={13} aria-hidden />}
                >
                  Delete player
                </Button>
              </div>
            )}
          </section>
        </div>
      </div>

      {deleteOpen && (
        <DeletePlayerDialog
          projectId={projectId}
          player={player}
          onClose={() => setDeleteOpen(false)}
          onDeleted={onDeleted}
        />
      )}

      {/* ───────── Sticky save bar ───────── */}
      {dirty && (
        <div className={styles.saveBar} role="region" aria-label="Unsaved changes">
          <div className={styles.inner}>
            <div
              className={`${styles.status} ${hasError ? styles.statusErr : ""}`}
            >
              <span className={styles.statusDot} aria-hidden />
              {hasError
                ? `Fix ${errorCount} ${
                    errorCount === 1 ? "error" : "errors"
                  } to save`
                : `${changeCount} unsaved ${
                    changeCount === 1 ? "change" : "changes"
                  }`}
            </div>
            <div className={styles.saveActions}>
              <Button
                variant="ghost"
                onClick={doDiscard}
                disabled={mutation.isPending}
              >
                Discard
              </Button>
              <Button
                variant="primary"
                onClick={doSave}
                disabled={hasError || mutation.isPending}
                loading={mutation.isPending}
                icon={
                  !mutation.isPending ? (
                    <Save size={13} aria-hidden />
                  ) : undefined
                }
              >
                {mutation.isPending ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// === Signature failures ============================================= //

/**
 * The identity card's fifth cell: how many of this player's requests were
 * refused over the counter's window.
 *
 * It is ALWAYS drawn once the answer is in — a missing cell would read as
 * "clean", and the four things it can say are four different statements:
 *
 *   a number  — refusals worth naming a player over;
 *   `0`       — the counter looked and found none;
 *   `—`       — signing is off, so nothing was ever counted and a zero here
 *               would claim an innocence nobody checked;
 *   `no data` — the counter store did not answer.
 *
 * Nothing sits beside it. There is no ban and no flag in this platform (§8.5),
 * and a button next to this number would turn an indicator into a verdict; the
 * operator looks at the whole player and uses the levers that already exist.
 */
function SignatureFailuresCell({
  data,
  loading,
  failed,
}: {
  data: PlayerSignatureFailures | undefined;
  loading: boolean;
  failed: boolean;
}) {
  // Nothing is claimed while the answer is in flight — "no data" would be a
  // statement, and this one is not true yet.
  if (loading && !failed) return null;

  const label = `Signature failures · ${data?.window_days ?? 7}d`;
  // A div like the four cells beside it: .kvVal carries a top margin, and an
  // inline element would quietly drop it and sit 2px tighter than its row.
  let value = <div className={`${styles.kvVal} ${styles.kvNone}`}>no data</div>;
  let gloss: ReactNode = null;

  if (!failed && data) {
    if (data.mode === "off") {
      value = <div className={`${styles.kvVal} ${styles.kvNone}`}>—</div>;
      gloss = (
        <>
          signing is <code>off</code> for this project
        </>
      );
    } else if (data.available) {
      value =
        data.count > 0 ? (
          <div className={`${styles.kvVal} ${styles.kvBad}`}>{data.count}</div>
        ) : (
          <div className={styles.kvVal}>0</div>
        );
      // Only where there is something to explain: what this number does NOT
      // include is the question a zero cannot raise.
      gloss =
        data.count > 0 ? "invalid · replay · unsupported format" : null;
    }
  }

  return (
    <div className={`${styles.kv} ${styles.kvSecondRow}`}>
      <div className={styles.kvKey}>{label}</div>
      {value}
      {gloss && <div className={styles.kvGloss}>{gloss}</div>}
    </div>
  );
}

// === Identity meta summary ========================================== //

interface MetaSummaryProps {
  schema: ProfileConfig["meta_schema"];
  meta: Record<string, unknown>;
}

/**
 * Read-only chip row under the player's name — "what has this player got on"
 * at a glance, without scrolling to the Meta card.
 *
 * Only the string-ish cosmetics become chips: an `int` or `bool` (prestige
 * level, VIP flag) is a counter, not something worn, and reads as noise here.
 * A single-value field constrained by an allow-list is the catalogued cosmetic
 * proper, so it gets the accent treatment.
 */
function MetaSummary({ schema, meta }: MetaSummaryProps) {
  const chips: { key: string; value: string; accent: boolean }[] = [];
  for (const f of schema) {
    const raw = meta[f.key];
    if (f.type === "string") {
      if (typeof raw === "string" && raw !== "") {
        chips.push({
          key: f.key,
          value: raw,
          accent: (f.allowed ?? []).length > 0,
        });
      }
    } else if (f.type === "string[]" && Array.isArray(raw)) {
      raw.forEach((v, i) => {
        if (typeof v === "string" && v !== "") {
          chips.push({ key: `${f.key}-${i}`, value: v, accent: false });
        }
      });
    }
  }
  if (chips.length === 0) return null;

  return (
    <div className={styles.metaSummary}>
      {chips.map((c) => (
        <span
          key={c.key}
          className={`${styles.pillTag} ${c.accent ? styles.pillTagAccent : ""}`}
        >
          {c.value}
        </span>
      ))}
    </div>
  );
}

// === Linked accounts ================================================ //

/** Provider names as the admin spells them out; anything else is title-cased. */
const PROVIDER_LABELS: Record<string, string | undefined> = {
  facebook: "Facebook",
  anonymous: "Anonymous (device)",
};

function providerLabel(provider: string): string {
  return (
    PROVIDER_LABELS[provider] ??
    provider.charAt(0).toUpperCase() + provider.slice(1)
  );
}

/** Sort key for the card's row order: the device identity goes last. */
function anonRank(provider: string): number {
  return provider === "anonymous" ? 1 : 0;
}

/** The glyph inside the 28px provider mark — the brand "f", or a device sign. */
function providerGlyph(provider: string): string {
  if (provider === "facebook") return "f";
  if (provider === "anonymous") return "⌘";
  return providerLabel(provider).charAt(0);
}

interface LinkedAccountsCardProps {
  identities: PlayerIdentity[] | undefined;
  loading: boolean;
  failed: boolean;
  /** The player's live avatar URL — the identity matching it is its source. */
  playerAvatarUrl: string | null;
}

/**
 * Read-only list of the player's `player_identities` rows. Its query is not
 * part of the page's render gate, so every state — loading, failed, empty —
 * is drawn inside the card body and leaves the editor below usable.
 *
 * The server orders by creation, which puts the anonymous device identity —
 * almost always the oldest — on top. The card leads with the real accounts
 * instead and keeps the device row as the footnote it is.
 */
function LinkedAccountsCard({
  identities,
  loading,
  failed,
  playerAvatarUrl,
}: LinkedAccountsCardProps) {
  // Stable sort: the named providers keep their server order, `anonymous`
  // sinks to the bottom.
  const ordered = useMemo(
    () =>
      identities
        ? [...identities].sort(
            (a, b) => anonRank(a.provider) - anonRank(b.provider),
          )
        : undefined,
    [identities],
  );

  let body: ReactNode;
  if (loading) {
    body = <IdentityRowsSkeleton />;
  } else if (failed && !identities) {
    // A failed background refetch keeps the rows we already have on screen —
    // the note is for when the card has nothing else to show.
    body = (
      <div className={styles.idNote} role="status">
        Couldn&apos;t load linked accounts.
      </div>
    );
  } else if (!ordered || ordered.length === 0) {
    body = <div className={styles.idNote}>No linked accounts.</div>;
  } else {
    body = ordered.map((identity) => (
      <IdentityRow
        key={`${identity.provider}:${identity.subject}`}
        identity={identity}
        playerAvatarUrl={playerAvatarUrl}
      />
    ));
  }

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>Linked accounts</h3>
        <span className={`${styles.cardSub} ${styles.cardSubMono}`}>
          player_identities
        </span>
      </div>
      <div className={`${styles.cardBody} ${styles.idBody}`}>{body}</div>
      <div className={styles.cardHelper}>
        Read-only. Linking and unlinking happen in the game client; the Facebook
        name and avatar refresh on every Facebook login or link.
      </div>
    </div>
  );
}

interface IdentityRowProps {
  identity: PlayerIdentity;
  playerAvatarUrl: string | null;
}

function IdentityRow({ identity, playerAvatarUrl }: IdentityRowProps) {
  const isFacebook = identity.provider === "facebook";
  // The row whose picture the player record actually carries — a Facebook
  // identity whose avatar has since been superseded gets no badge.
  const isAvatarSource =
    !!identity.avatar_url && identity.avatar_url === playerAvatarUrl;
  // Facebook hands us the name and picture; an identity that came back with
  // neither would render an empty avatar next to an empty label.
  const showProfile =
    isFacebook && (!!identity.avatar_url || !!identity.display_name);

  return (
    <div className={styles.idRow}>
      <span
        className={`${styles.providerMark} ${
          isFacebook ? styles.facebook : styles.anon
        }`}
        aria-hidden
      >
        {providerGlyph(identity.provider)}
      </span>
      <div className={styles.idMain}>
        <div className={styles.idProvider}>
          {providerLabel(identity.provider)}
          {isAvatarSource && <Badge variant="info">avatar source</Badge>}
        </div>
        <div className={styles.idSubject}>{identity.subject}</div>
      </div>
      {showProfile && (
        <div className={styles.idFbProfile}>
          <Avatar
            name={identity.display_name ?? undefined}
            image={identity.avatar_url ?? undefined}
            size={28}
          />
          {identity.display_name && (
            <span className={styles.idFbName}>{identity.display_name}</span>
          )}
        </div>
      )}
      <div className={styles.idDate}>
        {identity.provider === "anonymous" ? "Created" : "Linked"}{" "}
        {formatDate(identity.linked_at)}
      </div>
    </div>
  );
}

/** Two placeholder identity rows — the shape a player almost always has. */
function IdentityRowsSkeleton() {
  return (
    <>
      {[0, 1].map((i) => (
        <div className={styles.idRow} key={i}>
          <Skeleton width={28} height={28} radius={999} />
          <div className={styles.skelLines}>
            <Skeleton width={110} height={13} />
            <Skeleton width={150} height={11} />
          </div>
        </div>
      ))}
    </>
  );
}

/**
 * Deep JSON-equality check for a JSONB bag (stats or meta): compares every
 * key's value via JSON.stringify, independent of key order.
 */
function jsonObjectEqual(
  a: Record<string, unknown>,
  b: Record<string, unknown>,
): boolean {
  const ak = Object.keys(a).sort();
  const bk = Object.keys(b).sort();
  if (ak.length !== bk.length) return false;
  for (let i = 0; i < ak.length; i++) {
    if (ak[i] !== bk[i]) return false;
    if (JSON.stringify(a[ak[i]]) !== JSON.stringify(b[bk[i]])) return false;
  }
  return true;
}

// === Loading skeleton =============================================== //
// Identity card + linked accounts + 3 form cards, per the mockup state notes.

function LoadingSkeleton() {
  return (
    <>
      <div className={styles.card}>
        <div className={styles.cardBody}>
          <div className={styles.identity}>
            <Skeleton width={52} height={52} radius={999} />
            <div className={styles.skelLines}>
              <Skeleton width={160} height={16} />
              <Skeleton width={120} height={11} />
            </div>
          </div>
          <div className={styles.kvGrid}>
            {[0, 1, 2, 3].map((i) => (
              <div className={styles.kv} key={i}>
                <Skeleton width={70} height={9} />
                <Skeleton width="80%" height={13} style={{ marginTop: 4 }} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <Skeleton width={110} height={14} />
        </div>
        <div className={`${styles.cardBody} ${styles.idBody}`}>
          <IdentityRowsSkeleton />
        </div>
      </div>

      {[0, 1, 2].map((i) => (
        <div className={styles.card} key={i}>
          <div className={styles.cardHeader}>
            <Skeleton width={90} height={14} />
          </div>
          <div className={styles.cardBody}>
            <Skeleton width={120} height={12} />
            <Skeleton
              width="100%"
              height={36}
              radius={6}
              style={{ marginTop: 6 }}
            />
          </div>
        </div>
      ))}
    </>
  );
}
